/**
 * Diagnostics sessions — asking somebody if we may watch their device, and watching.
 *
 * These live on the **User API** (`/api/...`), not the Admin API, because the hub they pair with
 * is only mapped on the User API host — the Admin host maps no hubs at all. Requesting them as
 * `/admin/...` would send them somewhere with no diagnostics controller and no hub to join.
 */

import ApiService from '../apiService'

export interface DiagnosticsGrant {
  id: string
  subjectUserId: string
  /** Filled on the staff lists (issued, invite); null when the account is gone. */
  subjectDisplayName?: string | null
  subjectEmail?: string | null
  invitedByUserId: string
  reason?: string | null
  status: string
  createdAt: string
  expiresAt: string
  acceptedAt?: string | null
  eventCount: number
  frameCount: number
  commandCount: number
  /** What was asked for and agreed to. Watching implies neither. */
  allowsScreen: boolean
  allowsControl: boolean
  isStreamable: boolean
  /**
   * The powers resolved against the window as well as the flag — the server's answer, not ours.
   *
   * Sent rather than computed here so a lapsed session never offers a Capture button that cannot
   * work: "may I, right now" is answered by the same code that enforces it.
   */
  canCaptureScreen: boolean
  canControl: boolean
}

/** One batch of events the server kept, as the read-back endpoint returns it. */
export interface DiagnosticsBatch {
  receivedAt: string
  /** The device's own JSON array of events. */
  events: string
}

export const diagnosticsApi = {
  /**
   * Ask somebody. They get a notice; nothing happens until they accept.
   *
   * The two extra asks default to false here as well as on the server, so a caller that does not
   * think about them asks for the least.
   */
  invite: (
    subjectUserId: string,
    reason?: string,
    options?: { allowsScreen?: boolean; allowsControl?: boolean }
  ) =>
    ApiService.post<DiagnosticsGrant>('/api/diagnostics/invites', {
      subjectUserId,
      reason,
      allowsScreen: options?.allowsScreen ?? false,
      allowsControl: options?.allowsControl ?? false,
    }),

  /** What this staff member currently has open — invited or accepted. */
  issued: () => ApiService.get<DiagnosticsGrant[]>('/api/diagnostics/invites'),

  /**
   * What the session has kept, without opening a socket.
   *
   * The hub is the live path; this is the one that still works when it will not connect, and it
   * asks nothing of the device — the events are already on the server.
   */
  recent: (grantId: string) =>
    ApiService.get<DiagnosticsBatch[]>(`/api/diagnostics/${grantId}/recent`),

  /** End one early. */
  revoke: (grantId: string) =>
    ApiService.post<{ message: string }>(`/api/diagnostics/${grantId}/revoke`, {}),
}
